/* src/pages/Profile/MyProfile.js */
import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom"; // ✅ Cleaned up double import
import AuthContext from "../../context/AuthContext";
import { authFetch } from "../../Utilities/Api";
import ProfilePostsFeed from "../../components/ProfilePosts/ProfilePostsFeed";
import ProfileImage from "../../components/ProfileImage";
import "./MyProfile.css";

const MyProfile = () => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [activeTab, setActiveTab] = useState('posts');

    const { logout } = useContext(AuthContext);
    const navigate = useNavigate();

    useEffect(() => {
        let isMounted = true;

        const fetchProfile = async () => {
            try {
                console.log("🔍 [MyProfile] Fetching /users/profile...");
                const res = await authFetch("/users/profile");

                if (!isMounted) return;

                if (res.ok) {
                    const data = await res.json();
                    console.log("📥 [MyProfile] Backend Response:", data);
                    setProfile(data);
                } else if (res.status === 401) {
                    logout();
                    navigate("/login");
                } else {
                    setError("Failed to load profile.");
                }
            } catch (err) {
                console.error("❌ [MyProfile] Error:", err);
                if (isMounted) setError("An error occurred while loading profile.");
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchProfile();
        return () => { isMounted = false; };
    }, [logout, navigate]);

    if (loading) return <div className="profile-container"><div className="spinner"></div></div>;
    if (error) return <div className="profile-container">{error}</div>;
    if (!profile) return <div className="profile-container">No profile data found.</div>;

    const coverImage = profile.coverPicture || "https://via.placeholder.com/1200x300/3b82f6/ffffff?text=Cover+Image";

    // Parse Dates
    const joinDate = profile.createdAt ? new Date(profile.createdAt).toLocaleDateString() : null;
    const birthDate = profile.birthdate ? new Date(profile.birthdate).toLocaleDateString() : null;

    return (
        <div className="profile-container">

            {/* HEADER / COVER IMAGE */}
            <div className="profile-header">
                <div
                    className="cover-image"
                    style={{ backgroundImage: `url(${coverImage})` }}
                >
                    {/* ✅ Pointing to /settings now */}
                    <button
                        className="edit-cover-btn"
                        onClick={() => navigate('/settings')}
                    >
                        📷 Edit Cover
                    </button>
                </div>
            </div>

            {/* MAIN CONTENT CARD */}
            <div className="profile-content">

                {/* Identity Section */}
                <div className="profile-identity">
                    <div className="avatar-wrapper">
                        <ProfileImage
                            src={profile.profilePicture}
                            className="avatar-img-large"
                            alt={profile.username}
                        />
                        <div className="status-indicator online"></div>
                    </div>

                    <h1 className="username">{profile.nickname || profile.username}</h1>
                    <p className="user-handle">@{profile.username}</p>

                    {profile.bio && <p className="bio-text">{profile.bio}</p>}

                    <div className="profile-stats">
                        <span><strong>{profile.friendsCount || 0}</strong> Friends</span>
                        {profile.location && (
                            <>
                                <span>•</span>
                                <span>{profile.location}</span>
                            </>
                        )}
                    </div>

                    <div className="profile-actions">
                        {/* ✅ Main Edit button already points to /settings via Link */}
                        <Link to="/settings">
                            <button className="btn-primary">Edit Profile</button>
                        </Link>
                        <button className="btn-secondary" onClick={() => logout()}>Log Out</button>
                    </div>
                </div>

                {/* Tabs Section */}
                <div className="profile-tabs">
                    <button className={activeTab === 'posts' ? 'active' : ''} onClick={() => setActiveTab('posts')}>
                        My Posts
                    </button>
                    <button className={activeTab === 'about' ? 'active' : ''} onClick={() => setActiveTab('about')}>
                        About
                    </button>
                    <button className={activeTab === 'friends' ? 'active' : ''} onClick={() => setActiveTab('friends')}>
                        Friends
                    </button>
                </div>

                {/* Tab Content */}
                <div className="tab-content">
                    {activeTab === 'posts' && (
                        <div className="fade-in">
                            <ProfilePostsFeed profileUserId={profile._id} isOwnProfile={true} />
                        </div>
                    )}

                    {activeTab === 'about' && (
                        <div className="about-section fade-in">
                            <div className="info-card">
                                <h3>My Details</h3>
                                <div className="info-row">
                                    <span className="label">Username</span>
                                    <span className="value">@{profile.username}</span>
                                </div>
                                {profile.email && (
                                    <div className="info-row">
                                        <span className="label">Email</span>
                                        <span className="value">{profile.email}</span>
                                    </div>
                                )}
                                {profile.location && (
                                    <div className="info-row">
                                        <span className="label">Location</span>
                                        <span className="value">{profile.location}</span>
                                    </div>
                                )}
                                {birthDate && (
                                    <div className="info-row">
                                        <span className="label">Birthday</span>
                                        <span className="value">{birthDate}</span>
                                    </div>
                                )}
                                {joinDate && (
                                    <div className="info-row">
                                        <span className="label">Joined</span>
                                        <span className="value">{joinDate}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'friends' && (
                        <div className="placeholder-tab fade-in" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                            <p>You have <strong>{profile.friendsCount || 0}</strong> friends connected.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MyProfile;